// ============================================================
// Lume / TEACare PWA — ThemeProvider
// ============================================================
// Gerencia 2 dimensões de personalização visual:
//   - mode: 'light' | 'dark' | 'auto' (segue prefers-color-scheme)
//   - contrast: 'normal' | 'high' (força WCAG AAA contrast)
//
// Por que dark mode no Lume?
//   - Autistas N1 com fotossensibilidade frequente preferem ambientes
//     escuros (literatura sensorial).
//   - Cuidadores em uso noturno (Modo Crise às 3h da manhã, registro
//     pós-meltdown) — luz alta no rosto piora sensibilidade.
//   - Profissionais em plantão noturno usam tela escura por horas.
//
// Por que NÃO usamos preto absoluto:
//   - Princípio low-stim do CLAUDE.md.
//   - Texto branco sobre preto absoluto cria halo "shimmering" para
//     pessoas com sensibilidade visual.
// ============================================================

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type ThemeMode = 'light' | 'dark' | 'auto';
type ContrastMode = 'normal' | 'high';

interface ThemeState {
  mode: ThemeMode;
  contrast: ContrastMode;
  resolvedTheme: 'light' | 'dark';
}

interface ThemeApi extends ThemeState {
  setMode: (m: ThemeMode) => void;
  setContrast: (c: ContrastMode) => void;
}

const ThemeContext = createContext<ThemeApi | null>(null);

const STORAGE_KEY_MODE = 'lume.theme.mode';
const STORAGE_KEY_CONTRAST = 'lume.theme.contrast';

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY_MODE);
    if (v === 'light' || v === 'dark' || v === 'auto') return v;
  } catch {
    /* localStorage indisponível (SSR ou modo privado) */
  }
  return 'auto';
}

function readStoredContrast(): ContrastMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY_CONTRAST);
    if (v === 'normal' || v === 'high') return v;
  } catch {
    /* idem */
  }
  // Honra prefers-contrast: more do SO/navegador como default.
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-contrast: more)').matches) return 'high';
  }
  return 'normal';
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'light' || mode === 'dark') return mode;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Para testes unitários. */
  initialMode?: ThemeMode;
  initialContrast?: ContrastMode;
}

export function ThemeProvider({
  children,
  initialMode,
  initialContrast,
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(
    () => initialMode ?? readStoredMode(),
  );
  const [contrast, setContrastState] = useState<ContrastMode>(
    () => initialContrast ?? readStoredContrast(),
  );
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    resolveTheme(initialMode ?? readStoredMode()),
  );

  // Aplica data-theme e data-contrast no <html>.
  useEffect(() => {
    const root = document.documentElement;
    const theme = resolveTheme(mode);
    setResolvedTheme(theme);
    root.dataset.theme = theme;
    root.dataset.contrast = contrast === 'high' ? 'high' : '';
    if (contrast !== 'high') delete root.dataset.contrast;
  }, [mode, contrast]);

  // Reage a mudança do prefers-color-scheme do SO se o user escolheu auto.
  useEffect(() => {
    if (mode !== 'auto' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setResolvedTheme(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  const api = useMemo<ThemeApi>(
    () => ({
      mode,
      contrast,
      resolvedTheme,
      setMode: (m) => {
        setModeState(m);
        try {
          localStorage.setItem(STORAGE_KEY_MODE, m);
        } catch {
          /* sem persistência — UX continua, só não sobrevive a reload */
        }
      },
      setContrast: (c) => {
        setContrastState(c);
        try {
          localStorage.setItem(STORAGE_KEY_CONTRAST, c);
        } catch {
          /* idem */
        }
      },
    }),
    [mode, contrast, resolvedTheme],
  );

  return <ThemeContext.Provider value={api}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeApi {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme deve ser usado dentro de <ThemeProvider>');
  }
  return ctx;
}
